import { Injectable } from '@angular/core';

import { get, isNumber, isObject, isString } from 'lodash-es';
import assert from 'tiny-invariant';

import { Currency, CURRENCY_DEFAULT, Money } from './money.model';

@Injectable({
  providedIn: 'root',
})
export class MoneyOperationService {
  /**
   * The canonical zero-money value (`Rp0`). Inject `MoneyOperationService` and
   * use `this.moneyOperationService.MONEY_ZERO` instead of inline
   * `{ amount: '0.00', currency: 'IDR' }` literals.
   */
  readonly MONEY_ZERO: Money = {
    amount: '0.00',
    currency: CURRENCY_DEFAULT,
  };

  /**
   * Check if value is Money.
   */
  isMoney(value: unknown): value is Money {
    return (
      isObject(value) &&
      isString(get(value, 'amount')) &&
      isNumber(Number(get(value, 'amount'))) &&
      !isNaN(Number(get(value, 'amount'))) &&
      isString(get(value, 'currency'))
    );
  }

  /**
   * Converts the Money object to number, discarding their currencies.
   */
  moneyToNumber(money: Money): number {
    return Number(money.amount);
  }

  /**
   * Converts number to money with two decimal places.
   */
  numberToMoney(number: number, currency: Currency): Money {
    return {
      amount: number.toFixed(2),
      currency: currency,
    };
  }

  private verifyCompatible(first: Money, second: Money) {
    assert(first.currency === second.currency);
  }

  // DO NOT ADD MULTIPLICATION OR DIVISION for general-purpose money math.
  // These computations are "destructive" and should be done on the server.
  // Exception: moneyFloorTo / moneyCeilTo use integer denomination rounding
  // for POS display purposes only (e.g., floor/ceil to nearest 100 IDR).
  // Addition can be added though.

  /**
   * Returns "base" + "amountAdded"
   */
  moneyAdd(base: Money, amountAdded: Money): Money {
    this.verifyCompatible(base, amountAdded);
    return this.numberToMoney(
      this.moneyToNumber(base) + this.moneyToNumber(amountAdded),
      base.currency,
    );
  }

  /**
   * Returns "base" - "amountSubtracted"
   */
  moneySubtract(base: Money, amountSubtracted: Money): Money {
    this.verifyCompatible(base, amountSubtracted);
    return this.numberToMoney(
      this.moneyToNumber(base) - this.moneyToNumber(amountSubtracted),
      base.currency,
    );
  }

  /**
   * Returns "base" * "multiplier"
   */
  moneyMultiply(base: Money, multiplier: number): Money {
    return this.numberToMoney(
      this.moneyToNumber(base) * multiplier,
      base.currency,
    );
  }

  /**
   * Returns true if money amount is zero, otherwise false.
   */
  isMoneyZero(base: Money): boolean {
    return !this.moneyToNumber(base);
  }

  /**
   * Returns true if money decimal amount is zero, otherwise false.
   */
  isMoneyDecimalZero(base: Money): boolean {
    return Number(base.amount) % 1 === 0;
  }

  /**
   * Creates a zero money with the same currency with another money.
   */
  makeZero(base: Money): Money {
    return {
      amount: '0.00',
      currency: base.currency,
    };
  }

  /**
   * Returns true if money1 > money2
   */
  isGreaterThan(money1: Money, money2: Money): boolean {
    return this.moneyToNumber(money1) > this.moneyToNumber(money2);
  }

  /**
   * Returns true if money1 == money2, whatever the amounts' decimal places.
   */
  isEqual(money1: Money, money2: Money): boolean {
    this.verifyCompatible(money1, money2);
    return this.moneyToNumber(money1) === this.moneyToNumber(money2);
  }

  /**
   * Returns true if money1 >= money2
   */
  isGreaterThanOrEqual(money1: Money, money2: Money): boolean {
    return this.moneyToNumber(money1) >= this.moneyToNumber(money2);
  }

  isMoneyNegative(money: Money): boolean {
    return this.moneyToNumber(money) < 0;
  }

  /**
   * Floors a money amount down to the nearest multiple of the given
   * denominator.
   *
   * @example
   * moneyFloorTo({ amount: '3158.70', currency: 'IDR' }, 100)
   * // → { amount: '3100.00', currency: 'IDR' }
   *
   * moneyFloorTo({ amount: '3158.70', currency: 'IDR' }, 1)
   * // → { amount: '3158.00', currency: 'IDR' }
   */
  moneyFloorTo(money: Money, denominator: number): Money {
    assert(denominator > 0);
    const value = this.moneyToNumber(money);
    const floored = Math.floor(value / denominator) * denominator;
    return this.numberToMoney(floored, money.currency);
  }

  /**
   * Ceils a money amount up to the nearest multiple of the given
   * denominator.
   *
   * @example
   * moneyCeilTo({ amount: '41.30', currency: 'IDR' }, 100)
   * // → { amount: '100.00', currency: 'IDR' }
   *
   * moneyCeilTo({ amount: '-58.70', currency: 'IDR' }, 100)
   * // → { amount: '0.00', currency: 'IDR' }
   */
  moneyCeilTo(money: Money, denominator: number): Money {
    assert(denominator > 0);
    const value = this.moneyToNumber(money);
    const ceiled = Math.ceil(value / denominator) * denominator;
    return this.numberToMoney(ceiled, money.currency);
  }
}
